import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { toast } from 'sonner';
import {
  ClipboardList, CheckCircle2, Clock, FileQuestion,
  Search, Star, PenLine, Save, User, Sparkles, Quote
} from 'lucide-react';
import { diagnosticService } from '../services/api';

const NIVEAUX = [
  { value: 'non_acquis', label: 'Non acquis', color: 'bg-red-100 text-red-700', stars: 1 },
  { value: 'en_cours', label: 'En cours', color: 'bg-amber-100 text-amber-700', stars: 2 },
  { value: 'acquis', label: 'Acquis', color: 'bg-emerald-100 text-emerald-700', stars: 3 },
  { value: 'maitrise', label: 'Maîtrisé', color: 'bg-blue-100 text-blue-700', stars: 4 }
];

const CATEGORIES = [
  { cle: 'transversale', optionsKey: 'competences_transversales', titre: 'Compétences transversales' },
  { cle: 'technique', optionsKey: 'competences_techniques', titre: 'Compétences techniques' },
  { cle: 'personnelle', optionsKey: 'competences_personnelles', titre: 'Compétences personnelles' }
];

const STATUTS = {
  absent: { label: 'À saisir', color: 'bg-slate-100 text-slate-700', icon: FileQuestion },
  en_cours: { label: 'Brouillon', color: 'bg-slate-100 text-slate-700', icon: PenLine },
  soumis: { label: 'À valider', color: 'bg-amber-100 text-amber-700', icon: Clock },
  rejete: { label: 'À revoir', color: 'bg-red-100 text-red-700', icon: Clock },
  valide: { label: 'Validé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 }
};

const getNiveauInfo = (value) => NIVEAUX.find((n) => n.value === value) || null;

const ValidationDiagnostics = () => {
  const [options, setOptions] = useState({
    competences_transversales: [],
    competences_techniques: [],
    competences_personnelles: []
  });
  const [lignes, setLignes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recherche, setRecherche] = useState('');
  const [filtre, setFiltre] = useState('tous');

  // Saisie en cours
  const [selection, setSelection] = useState(null);
  const [niveaux, setNiveaux] = useState({});
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [optionsRes, suiviRes] = await Promise.all([
        diagnosticService.getOptions(),
        diagnosticService.getSuivi()
      ]);
      setOptions(optionsRes.data);
      setLignes(suiviRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de chargement des diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const compteurs = useMemo(() => ({
    aSaisir: lignes.filter((l) => l.statut === 'absent' || l.statut === 'en_cours').length,
    propositions: lignes.filter((l) => l.proposition_automatique && l.statut !== 'valide').length,
    aValider: lignes.filter((l) => l.statut === 'soumis' || l.statut === 'rejete').length,
    valides: lignes.filter((l) => l.statut === 'valide').length
  }), [lignes]);

  const lignesFiltrees = useMemo(() => {
    const terme = recherche.trim().toLowerCase();
    return lignes.filter((l) => {
      const nomComplet = `${l.prenom} ${l.nom}`.toLowerCase();
      if (terme && !nomComplet.includes(terme)) return false;
      if (filtre === 'a_saisir') return l.statut === 'absent' || l.statut === 'en_cours';
      if (filtre === 'a_valider') return l.statut === 'soumis' || l.statut === 'rejete';
      if (filtre === 'valides') return l.statut === 'valide';
      return true;
    });
  }, [lignes, recherche, filtre]);

  const toutesCompetences = useMemo(() => (
    CATEGORIES.flatMap((cat) =>
      (options[cat.optionsKey] || []).map((competence) => ({ competence, categorie: cat.cle }))
    )
  ), [options]);

  const ouvrirSaisie = (ligne) => {
    const prefill = {};
    (ligne.evaluations || []).forEach((ev) => {
      prefill[ev.competence] = ev.niveau_valide || ev.niveau_auto || null;
    });
    setNiveaux(prefill);
    setCommentaire('');
    setSelection(ligne);
  };

  const fermerSaisie = () => {
    setSelection(null);
    setNiveaux({});
    setCommentaire('');
  };

  const choisirNiveau = (competence, niveau) => {
    setNiveaux((prev) => ({
      ...prev,
      [competence]: prev[competence] === niveau ? null : niveau
    }));
  };

  // Extrait de compte-rendu motivant un niveau pré-rempli
  const justification = (competence) => {
    if (!selection?.proposition_automatique) return null;
    const ev = (selection.evaluations || []).find((e) => e.competence === competence);
    return ev?.justification || null;
  };

  // Auto-évaluation déposée par le bénéficiaire, affichée à titre indicatif
  const autoEvaluation = (competence) => {
    const ev = (selection?.evaluations || []).find((e) => e.competence === competence);
    if (!ev || selection?.saisi_par_intervenant) return null;
    return getNiveauInfo(ev.niveau_auto);
  };

  const nbRenseignees = Object.values(niveaux).filter(Boolean).length;

  // Un diagnostic déposé par le bénéficiaire passe par la route de validation,
  // afin de ne pas écraser son auto-évaluation ni la traçabilité de la saisie.
  const estSaisieIntervenant = !selection
    || selection.statut === 'absent'
    || selection.statut === 'en_cours'
    || selection.saisi_par_intervenant;

  const enregistrer = async (statut) => {
    if (!selection) return;
    if (nbRenseignees === 0) {
      toast.error('Renseignez au moins une compétence');
      return;
    }

    const evaluations = toutesCompetences
      .filter(({ competence }) => niveaux[competence])
      .map(({ competence, categorie }) => {
        const existante = (selection.evaluations || []).find((e) => e.competence === competence) || {};
        return {
          ...existante,
          competence,
          categorie: existante.categorie || categorie,
          niveau_valide: niveaux[competence]
        };
      });

    setSubmitting(true);
    try {
      if (estSaisieIntervenant) {
        await diagnosticService.saisirPourApprenant(selection.apprenant_id, {
          evaluations,
          statut,
          commentaire: commentaire.trim() || null
        });
      } else {
        await diagnosticService.valider(selection.diagnostic_id, { evaluations });
      }
      toast.success(statut === 'valide' ? 'Diagnostic validé' : 'Brouillon enregistré');
      fermerSaisie();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-white rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-slate-500">Chargement des diagnostics...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <ClipboardList className="w-6 h-6 inline mr-2 text-blue-600" />
            Diagnostics de compétences
          </h2>
          <p className="text-slate-600">
            Saisissez le diagnostic pendant l'entretien ou validez celui déposé par le bénéficiaire.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Badge className="bg-slate-100 text-slate-700 px-3 py-1">{compteurs.aSaisir} à saisir</Badge>
          {compteurs.propositions > 0 && (
            <Badge className="bg-violet-100 text-violet-700 px-3 py-1">
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              {compteurs.propositions} pré-remplis à vérifier
            </Badge>
          )}
          <Badge className="bg-amber-100 text-amber-700 px-3 py-1">{compteurs.aValider} à valider</Badge>
          <Badge className="bg-emerald-100 text-emerald-700 px-3 py-1">{compteurs.valides} validés</Badge>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={recherche}
            onChange={(e) => setRecherche(e.target.value)}
            placeholder="Rechercher un bénéficiaire"
            className="pl-9 rounded-xl"
            data-testid="diagnostic-search"
          />
        </div>
        {[
          { cle: 'tous', label: 'Tous' },
          { cle: 'a_saisir', label: 'À saisir' },
          { cle: 'a_valider', label: 'À valider' },
          { cle: 'valides', label: 'Validés' }
        ].map((f) => (
          <Button
            key={f.cle}
            variant={filtre === f.cle ? 'default' : 'outline'}
            onClick={() => setFiltre(f.cle)}
            className={`rounded-xl ${filtre === f.cle ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white'}`}
            data-testid={`diagnostic-filtre-${f.cle}`}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Liste */}
      {lignesFiltrees.length === 0 ? (
        <Card className="bg-slate-50 rounded-3xl">
          <CardContent className="p-8 text-center">
            <FileQuestion className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-semibold text-slate-700">Aucun bénéficiaire ne correspond</h3>
            <p className="text-slate-500 mt-1">Modifiez la recherche ou le filtre.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {lignesFiltrees.map((ligne) => {
            const statut = STATUTS[ligne.statut] || STATUTS.absent;
            const StatutIcon = statut.icon;
            const aSaisir = ligne.statut === 'absent' || ligne.statut === 'en_cours';
            const aValider = ligne.statut === 'soumis' || ligne.statut === 'rejete';
            const estProposition = ligne.proposition_automatique && ligne.statut !== 'valide';

            return (
              <Card key={ligne.apprenant_id} className="bg-white rounded-2xl shadow-sm">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold shrink-0">
                        {ligne.prenom?.[0]}{ligne.nom?.[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">
                          {ligne.prenom} {ligne.nom}
                        </p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
                          {ligne.zone_geographique && <span>{ligne.zone_geographique}</span>}
                          {ligne.nb_competences > 0 && <span>{ligne.nb_competences} compétences</span>}
                          {estProposition && (
                            <span>Pré-rempli d'après les comptes-rendus</span>
                          )}
                          {ligne.saisi_par_intervenant && ligne.saisi_par_nom && (
                            <span className="inline-flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Saisi par {ligne.saisi_par_nom}
                            </span>
                          )}
                          {!ligne.saisi_par_intervenant && ligne.statut === 'valide' && ligne.valide_par_nom && (
                            <span>Validé par {ligne.valide_par_nom}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge
                        className={`px-3 py-1 ${
                          estProposition ? 'bg-violet-100 text-violet-700' : statut.color
                        }`}
                      >
                        {estProposition ? (
                          <Sparkles className="w-3.5 h-3.5 mr-1" />
                        ) : (
                          <StatutIcon className="w-3.5 h-3.5 mr-1" />
                        )}
                        {estProposition ? 'Pré-rempli à vérifier' : statut.label}
                      </Badge>
                      <Button
                        onClick={() => ouvrirSaisie(ligne)}
                        className={`rounded-xl ${aValider ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                        data-testid={`diagnostic-action-${ligne.apprenant_id}`}
                      >
                        {aSaisir ? <PenLine className="w-4 h-4 mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                        {estProposition ? 'Vérifier' : aSaisir ? 'Saisir' : aValider ? 'Valider' : 'Modifier'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de saisie / validation */}
      <Dialog open={!!selection} onOpenChange={(ouvert) => !ouvert && fermerSaisie()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-semibold">
                {selection?.prenom?.[0]}{selection?.nom?.[0]}
              </div>
              <div>
                <span>{estSaisieIntervenant ? 'Saisie du diagnostic' : 'Validation du diagnostic'}</span>
                <p className="text-sm font-normal text-slate-500">
                  {selection?.prenom} {selection?.nom}
                </p>
              </div>
            </DialogTitle>
            <DialogDescription>
              {estSaisieIntervenant
                ? "Renseignez le niveau de chaque compétence constaté pendant l'entretien. Le bénéficiaire est informé de la saisie et peut consulter son diagnostic."
                : "Le bénéficiaire s'est auto-évalué. Indiquez le niveau que vous validez pour chaque compétence."}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {selection?.proposition_automatique && selection?.statut !== 'valide' && (
              <div className="mb-4 rounded-xl border border-violet-200 bg-violet-50 p-4">
                <p className="text-sm font-semibold text-violet-900 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Diagnostic pré-rempli à partir des comptes-rendus
                </p>
                <p className="text-sm text-violet-800 mt-1">
                  Ces niveaux sont une proposition, pas un constat. Corrigez ce qui ne correspond
                  pas à ce que vous observez, puis validez : votre validation remplace la
                  proposition et c'est votre nom qui figure sur le diagnostic.
                </p>
                <p className="text-xs text-violet-700 mt-2">
                  Les compétences dont les comptes-rendus ne disent rien sont restées vides :
                  à vous de les renseigner.
                </p>
              </div>
            )}
            <p className="text-sm text-slate-500 mb-4">
              {nbRenseignees} compétence(s) renseignée(s) sur {toutesCompetences.length}
            </p>

            <div className="space-y-6">
              {CATEGORIES.map((cat) => (
                <div key={cat.cle}>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                    {cat.titre}
                  </h4>
                  <div className="border border-slate-200 rounded-xl divide-y divide-slate-100">
                    {(options[cat.optionsKey] || []).map((competence) => {
                      const auto = autoEvaluation(competence);
                      const motif = justification(competence);
                      return (
                        <div key={competence} className="p-3 flex flex-wrap items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900">{competence}</p>
                            {auto && (
                              <p className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                                <Star className="w-3 h-3 fill-current text-amber-500" />
                                Auto-évaluation : {auto.label}
                              </p>
                            )}
                            {motif && (
                              <p className="text-xs text-violet-700 mt-1 flex items-start gap-1.5">
                                <Quote className="w-3 h-3 mt-0.5 shrink-0" />
                                <span>{motif}</span>
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1.5 shrink-0">
                            {NIVEAUX.map((niveau) => (
                              <button
                                key={niveau.value}
                                type="button"
                                onClick={() => choisirNiveau(competence, niveau.value)}
                                title={niveau.label}
                                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                  niveaux[competence] === niveau.value
                                    ? `${niveau.color} ring-2 ring-offset-1 ring-blue-500`
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                                data-testid={`niveau-${competence}-${niveau.value}`}
                              >
                                {niveau.stars} · {niveau.label}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {estSaisieIntervenant && (
              <div className="mt-6">
                <label htmlFor="diagnostic-commentaire" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Commentaire d'entretien (optionnel)
                </label>
                <Textarea
                  id="diagnostic-commentaire"
                  value={commentaire}
                  onChange={(e) => setCommentaire(e.target.value)}
                  placeholder="Contexte de la saisie, points d'appui, axes de progression…"
                  className="mt-2 rounded-xl"
                  rows={3}
                />
                <p className="text-xs text-slate-400 mt-1">
                  Conservé dans l'historique du diagnostic. Le bénéficiaire y a accès : restez factuel.
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={fermerSaisie} className="rounded-xl">
              Annuler
            </Button>
            {estSaisieIntervenant && (
              <Button
                variant="outline"
                onClick={() => enregistrer('en_cours')}
                disabled={submitting}
                className="rounded-xl"
                data-testid="diagnostic-save-draft"
              >
                <Save className="w-4 h-4 mr-2" />
                Enregistrer le brouillon
              </Button>
            )}
            <Button
              onClick={() => enregistrer('valide')}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-xl"
              data-testid="diagnostic-confirm"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {submitting ? 'Enregistrement...' : 'Valider le diagnostic'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ValidationDiagnostics;
